import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useQueryClient } from '@tanstack/react-query';
import moment from 'moment';

import {
  createEntityAppointment,
  rescheduleAppointmentRecurrence,
  updateAppointmentV2
} from 'api/Appointment';

import { formatDate } from 'lib/helpers/utility';

import { showAlert } from 'components/shared/Alert/Alert';
import Button from 'components/shared/Buttons/Button';
import Confirm from 'components/shared/Modal/Confirm/Confirm';

const RescheduleConfirmation = ({
  currentEvent,
  showConfirmationModal,
  setShowConfirmationModal,
  currentView
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const selectedDate = currentEvent?.start;

  const rescheduleAppointment = async (updatedAppointment) => {
    setIsLoading(true);
    const appointment = updatedAppointment.event.appointment;

    const params = {
      id: appointment.id,
      starts_at: updatedAppointment.start,
      ends_at: updatedAppointment.end,
      practitioner_id:
        currentView === 'provider' && updatedAppointment.resourceId
          ? updatedAppointment.resourceId
          : appointment.practitioner_id,
      resource_id:
        currentView === 'resource' && updatedAppointment.resourceId
          ? updatedAppointment.resourceId
          : updatedAppointment.resource_id || undefined
    };

    const { code } = await updateAppointmentV2(navigate, params);

    queryClient.invalidateQueries(['appointment-events']);
    if (code === 0) {
      showAlert({ message: 'Appointment was updated successfully!' });
      setShowConfirmationModal(false);
    }

    setIsLoading(false);
  };

  const handleCreateEntity = async (params) => {
    const { code } = await createEntityAppointment(navigate, params);

    if (code === 0) {
      //   queryClient.invalidateQueries(['appointment-events']);
      showAlert({ color: 'success', message: 'Appointment updated successfully!' });
    }

    if (code === 6) {
      showAlert({
        message: 'An appointment already exists at this time',
        color: 'warning',
        buttons: [
          {
            text: 'Reschedule anyway',
            onClick: () => handleCreateEntity({ ...params, isReschedule: false }),
            color: 'warning'
          }
        ]
      });
    }
  };

  const handleDateChange = async (updatedAppointment, type) => {
    const appointment = currentEvent.event.appointment;
    const starts_at = updatedAppointment.start;
    const ends_at = updatedAppointment.end;

    if (type === 'only') {
      if (appointment.parent_appointment_id) {
        await rescheduleAppointment(updatedAppointment);
        return;
      }
      setIsLoading(true);

      const params = {
        appointment_id: appointment.id,
        appointment_date: {
          starts_at,
          ends_at,
          deleted_date: formatDate(appointment.starts_at)
        },
        isReschedule: true
      };
      await handleCreateEntity(params);
      setTimeout(() => {
        // queryClient.invalidateQueries(['appointment-events']);
      }, 1000);
      setIsLoading(false);
      return;
    }

    if (type === 'all') {
      const params = {
        appointmentChanges: {
          id: appointment.id,
          starts_at,
          ends_at
        },
        recurrence: {
          base_end_date: moment(appointment.starts_at).subtract(1, 'day').toDate(),
          base_appointment: !!appointment?.is_base_appointment
        }
      };
      console.log('params', params);

      const { code } = await rescheduleAppointmentRecurrence(navigate, params);
      //   return;
      if (code === 0) {
        setTimeout(() => {
          //   queryClient.invalidateQueries(['appointment-events']);
        }, 1000);
        showAlert({ color: 'success', message: 'Appointment updated successfully!' });
      }
      setIsLoading(false);
    }
  };

  const handleClose = () => setShowConfirmationModal(false);

  return (
    <>
      {moment(selectedDate).isAfter(moment(new Date())) ? (
        <Confirm
          title="You're changing a repeating appointment!"
          message={`You're changing the date of a repeating appointment. Do you want to move only this recurrence to ${formatDate(selectedDate)}, or change the date for this and all future appointments?`}
          handleOpen={showConfirmationModal}
          customClassName="w-[720px]"
          icon="calendar-dates-mono"
          footer={
            <div className="flex items-center justify-between pt-4">
              <Button size="small" outlined text="Cancel" color="neutral" onClick={handleClose} />
              <div className="flex items-center gap-2">
                <Button
                  size="small"
                  text="Only this appointment"
                  color="primary"
                  onClick={() => handleDateChange(currentEvent, 'only')}
                  loading={isLoading}
                  disabled={isLoading}
                />
                <Button
                  size="small"
                  outlined
                  text="All future appointments"
                  color="primary"
                  onClick={() => handleDateChange(currentEvent, 'all')}
                  loading={isLoading}
                  disabled={isLoading}
                />
              </div>
            </div>
          }
          handleClose={handleClose}
        />
      ) : (
        <Confirm
          handleOpen={showConfirmationModal}
          handleClose={() => setShowConfirmationModal(false)}
          handleContinue={() => rescheduleAppointment(currentEvent)}
          primaryBtnTxt="Reschedule appointment"
          secondaryBtnTxt="Keep"
          title="Confirm reschedule"
          message={`Are you sure you want to reschedule this appointment to ${moment(
            currentEvent?.start
          )}`}
          icon="calendar-restore-mono"
          loading={isLoading}
        />
      )}
    </>
  );
};

export default RescheduleConfirmation;
